import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LeadsProvider } from './contexts/LeadsContext';

// Components
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardLayout from './components/Layout/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Leads from './pages/Leads';
import Projects from './pages/Projects';
import Remedials from './pages/Remedials';
import OnOrder from './pages/OnOrder';
import ProjectDetail from './pages/ProjectDetail';
import Invoices from './pages/Invoices';
import Documents from './pages/Documents';
import Calendar from './pages/Calendar';
import Settings from './pages/Settings';
import LeadEdit from './pages/LeadEdit';
import OrderBuild from './pages/OrderBuild';
import DesignerDemo from './pages/DesignerDemo';
import FollowUp from './pages/FollowUp';
import FittersDiary from './pages/FittersDiary';
import RemedialsDiary from './pages/RemedialsDiary';
import WonLeadsAnalytics from './pages/WonLeadsAnalytics';
import KitchenPreviewer from './pages/KitchenPreviewer';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2c3e50',
      light: '#34495e',
      dark: '#1a252f',
    },
    secondary: {
      main: '#3498db',
      light: '#5dade2',
      dark: '#2980b9',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#7f8c8d',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#2c3e50',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#2c3e50',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#2c3e50',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#2c3e50',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/" />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Dashboard />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/customers" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Customers />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/leads" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Leads />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/leads/new" element={
        <ProtectedRoute>
          <DashboardLayout>
            <LeadEdit />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/leads/:id/edit" element={
        <ProtectedRoute>
          <DashboardLayout>
            <LeadEdit />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/leads/:id/order-build" element={
        <ProtectedRoute>
          <DashboardLayout>
            <OrderBuild />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/follow-up" element={
        <ProtectedRoute>
          <DashboardLayout>
            <FollowUp />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Projects />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/remedials" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Remedials />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/on-order" element={
        <ProtectedRoute>
          <DashboardLayout>
            <OnOrder />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:id" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ProjectDetail />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/projects/:id/order-build" element={
        <ProtectedRoute>
          <DashboardLayout>
            <OrderBuild isProject={true} />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/remedials/:id/order-build" element={
        <ProtectedRoute>
          <DashboardLayout>
            <OrderBuild isRemedial={true} />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/invoices" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Invoices />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/documents" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Documents />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/designer-demo" element={
        <ProtectedRoute>
          <DashboardLayout>
            <DesignerDemo />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/fitters-diary" element={
        <ProtectedRoute>
          <DashboardLayout>
            <FittersDiary />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/remedials-diary" element={
        <ProtectedRoute>
          <DashboardLayout>
            <RemedialsDiary />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/analytics/won-leads" element={
        <ProtectedRoute>
          <DashboardLayout>
            <WonLeadsAnalytics />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/calendar" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Calendar />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Settings />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/kitchen-previewer" element={
        <ProtectedRoute>
          <DashboardLayout>
            <KitchenPreviewer />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Catch all route - redirect to login if not authenticated */}
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <LeadsProvider>
            <Router>
              <AppRoutes />
            </Router>
          </LeadsProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
